import { DownloadOutlined, EditOutlined } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getPaymentTokenAPI } from "api/quotes";
import { fetchSubmissionAPI, generatePdfForQuote, generateQuotesAPI, updateSiaApplicationAPI } from "api/siaApplication";
import applicationUtil from "components/SurveyJS/utils/applicationUtils";
import CustomButton from "components/common/CustomButton/CustomButton";
import PopupModal from "components/common/PopupModal/PopupModal";
import SectionInfo from "components/common/SectionInfo/SectionInfo";
import AnimatedButton from "components/custom/AnimatedButton";
import InstallmentSelectionModal from "./InstallmentSelectionModal";
import { useHomeownersInstallments } from "hooks/useHomeownersInstallments";
import CircularProgressLoader from "components/loader/Loader";
import {
  COUNTRY,
  FAIRFAX,
  IS_BROKER_PORTAL,
  LIBERSEGUROS,
  PORTAL_NAME,
  CountryCode,
} from "config";
import { animateRouteProps } from "constants/common";
import { otherConfigSelector } from "portalConfig/selectors";
import { setFromLocation } from "store/features/locationStateSlice";
import {
  resetQuotesForSendingEmail,
  sendQuoteForReview,
  sendQuotes,
  setAvailableQuotes,
  setInvalidQuotes,
  setPolling,
  setSentQuotesToken,
  setStatus,
  setJustGeneratedFromApplication,
} from "store/features/quotesSlice";
import { setLoadingValue } from "store/features/clientSlice";
import { setLoaderText } from "store/features/loaderSlice";
import { setUpdatedSIAApplication } from "store/features/surveyJsSlice";
import { CountryMapping } from "config";
import {
  authSelector,
  clientSelector,
  customStylesSelector,
  productsSelector,
  quotesSelector,
  surveyJSDataSelector,
} from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import { CountryToLanguageMapping, getProductTitle } from "utils/utils";
import ErrorMessage from "../ErrorMessage";
import SuccessMessage from "../SuccessMessage";
import {
  getApplicationEmail,
  getClientData,
  ProductIconMapping,
} from "../utils";
import ProductQuoteCard from "./ProductQuoteCard";
import QuotesMissing from "./QuotesMissing";
import ShareLinkButton from "./ShareLinkButton";
import Summary from "./Summary";
import { downloadFile } from "utils/file";
import { useCallback, useRef } from "react";
import { fromPairs } from "lodash";
import { setCustomerDetails } from "store/features/customerDetailsSlice";
import { updateStartPageSelection } from "store/features/clientSlice";

const MAX_POLLING_ATTEMPTS = 40;
const POLLING_INTERVAL = 5000;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </Box>
  );
}

interface QuoteProps {
  submission?: Submission;
  isFromDashboard?: boolean;
  status?: string;
  onQuotesSent?: () => void;
}

const Quote: React.FC<QuoteProps> = ({
  submission,
  isFromDashboard,
  status,
  onQuotesSent,
}) => {
  const { t, lang } = useT();
  const dispatch = useDispatch();
  const customStyles = useSelector(customStylesSelector);
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [intervalRef, setIntervalRef] = useState<NodeJS.Timer>();
  const [pollingCounter, setPollingCounter] = useState(0);
  const [downloadingQuotePdf, setDownloadingQuotePdf] = useState(false);
  const [isInstallmentModalOpen, setIsInstallmentModalOpen] = useState(false);
  const [installmentModalProcessed, setInstallmentModalProcessed] = useState(false);
  const [isProcessingInstallments, setIsProcessingInstallments] = useState(false);
  const { quotes, invalidQuotes, quoteStatus, justGeneratedFromApplication } = useSelector(
    (state: RootState) => ({
      quotes: state.quotes.availableQuotes,
      invalidQuotes: state.quotes.invalidQuotes,
      quoteStatus: state.quotes.status,
      justGeneratedFromApplication: state.quotes.justGeneratedFromApplication,
    })
  );
  const { clientAPIKey, externalAuthConfig, availableProducts: clientProducts, startPageSelection, productSelection } = useSelector(clientSelector);
  const selectedProducts = useSelector(productsSelector);
  const otherConfig = useSelector(otherConfigSelector);
  const pollingRef = useRef<NodeJS.Timer>();
  const pollingAttemptsRef = useRef(0);
  const polling = useSelector((state: RootState) => state.quotes.polling);
  const quoteConfig = useSelector(
    (state: RootState) => state.client.quoteConfig
  );
  
  // Check if current product is HOMEOWNERS-ALLIANZ
  const isHomeownersAllianz = selectedProducts.some(
    (product) =>
      product?.product?.product_identifier === "HOMEOWNERS" &&
      product?.carrier?.includes("ALLIANZ")
  ) || selectedProducts.some(
    (product) => product?.product?.product_identifier === "HOMEOWNERS-ALLIANZ"
  );

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = undefined;
    }
    dispatch(setPolling(false));
  }, [dispatch]);

  const fetchSubmission = async () => {
    try {
      const response = await fetchSubmissionAPI(submissionId, clientAPIKey);
      if (response.data) {
        const filteredQuotes = (response.data?.quotes || []).filter(
          (q) => q.status === "generated"
        );
        const invalidQuotes = (response.data?.quotes || []).filter(
          (q) => q.status === "invalid"
        );

        dispatch(setAvailableQuotes(filteredQuotes));
        dispatch(setInvalidQuotes(invalidQuotes));
        dispatch(setStatus(response.data?.status));

        if (response.data?.status !== "awaiting_webhook_response") {
          stopPolling();
        }
      }
    } catch (error) {
      console.error(error, "error in fetchSubmission");
      stopPolling();
    }
  };

  const startPolling = useCallback(() => {
    if (!pollingRef.current) {
      pollingAttemptsRef.current = 0;
      dispatch(setPolling(true));

      const poll = async () => {
        await fetchSubmission();
        pollingAttemptsRef.current += 1;

        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          stopPolling();
        }
      };

      poll();
      pollingRef.current = setInterval(poll, POLLING_INTERVAL);
    }
  }, [dispatch, stopPolling]);

  const {
    accounts: { selectedAccount },
  } = useSelector(authSelector);
  const {
    sentQuotesToken,
    selectedQuotesForSendingEmail,
    sendingQuotes,
    sentQuotesError,
  } = useSelector(quotesSelector);
  const { updatedSiaApplication, submissionId, siaApplicationId } = useSelector(
    (state: RootState) => state.surveyJs
  );
  const { selectedQuotes } = useSelector(quotesSelector);
  const { availableProducts } = useSelector(clientSelector);
  const { portalAccount } = useSelector(authSelector);
  const surveyJSData = useSelector(surveyJSDataSelector);
  
  // Extract packages from quotes for homeowners-allianz
  const { packages } = useHomeownersInstallments(quotes);
  const summaryData = isFromDashboard
    ? applicationUtil.generateSummaryData(
        submission?.application_questionnaire,
        submission?.application_params,
        lang
      )
    : applicationUtil.generateSummaryData(
        surveyJSData?.siaApplication?.survey_json,
        surveyJSData?.updatedSiaApplication?.params,
        lang,
        surveyJSData?.updatedSiaApplication?.questions
      );

  const selectedForSendingEmail = (productName: string) =>
    !!selectedQuotesForSendingEmail.find(
      (sq) =>
        sq.product_details?.type?.toLowerCase() === productName.toLowerCase()
    );

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleUpdateApplication = () => {
    const isFairFax = PORTAL_NAME === FAIRFAX;
    // Get the email value from the correct source
    const emailValue = getApplicationEmail(
      submission?.application_params?.products?.[0],
      submission?.application_params
    );
    dispatch(setCustomerDetails({ email: emailValue }));

    // Extract product information from the submission
    if (
      submission?.application_params?.products &&
      submission.application_params.products.length > 0
    ) {
      const selectedProds = clientProducts?.filter((p) =>
        submission.application_params.products.some((submissionProduct) => {
          const productId = p?.product?.product_identifier;
          const withCarrier = p?.carrier?.[0]
            ? `${productId}-${p?.carrier?.[0]}`
            : productId;

          // Check if this exact product is in the submission products
          return (
            submissionProduct === withCarrier ||
            // Or if it starts with the product ID (for flexibility)
            submissionProduct.startsWith(productId)
          );
        })
      );

      // Only update if we found matching products
      if (selectedProds && selectedProds.length > 0) {
        dispatch(updateStartPageSelection(selectedProds));
      } else {
        // If no products found with the new method, log this for debugging
        console.warn("No matching products found in availableProducts");
      }
    }

    localStorage.setItem(
      "clientData",
      JSON.stringify(
        getClientData(
          submission?.application_params?.products?.[0],
          submission?.application_params
        )
      )
    );
    dispatch(setFromLocation("/home/quotes"));
    navigate(`/newQuote?applicationId=${submission?.application_id}`);
  };

  const downloadQuotePdf = async () => {
    try {
      setDownloadingQuotePdf(true);
      const res = await generatePdfForQuote(quotes[0]?.id, quotes[0]?.account);
      downloadFile(res?.data?.download_url, `${res?.data?.file_name}.pdf`);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingQuotePdf(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInstallmentConfirm = async (
    selectedPackage: any,
    selectedInstallment: number,
    selectedPaymentMethod: number
  ) => {
    try {
      // Close modal and start processing
      setIsInstallmentModalOpen(false);
      setIsProcessingInstallments(true);
      
      // Show loading state
      dispatch(setLoaderText(t("UPDATING_APPLICATION")));
      dispatch(setLoadingValue(true));
      
      setInstallmentModalProcessed(true);

      // Get existing application params from updatedSiaApplication or surveyJSData
      const existingParams = updatedSiaApplication?.params || surveyJSData?.updatedSiaApplication?.params || {};
      
      // Prepare products array
      let products: string[] = [];
      if (productSelection && productSelection.length > 0) {
        products = productSelection;
      } else if (startPageSelection && startPageSelection.length > 0) {
        products = startPageSelection.map((p) => {
          const productId = p?.product?.product_identifier;
          return p?.carrier?.[0] ? `${productId}-${p?.carrier?.[0]}` : productId;
        });
      }

      // Prepare update payload - preserve all existing params and only update homeowners_installments
      const payload = {
        country: CountryMapping[COUNTRY],
        products: products,
        params: {
          ...existingParams, // Preserve ALL existing application params
          homeowners_installments: selectedInstallment, // Only update this field (1-10)
        },
      };

      // Update application
      const updateRes = await updateSiaApplicationAPI(
        externalAuthConfig ? null : clientAPIKey,
        siaApplicationId,
        payload
      );

      dispatch(setUpdatedSIAApplication(updateRes.data));

      // Regenerate quotes with the updated installment configuration
      dispatch(setLoaderText(t("FETCHING_PLANS")));
      
      const quotesRes = await generateQuotesAPI(
        externalAuthConfig ? null : clientAPIKey,
        submissionId,
        true // regenerate = true
      );

      dispatch(setStatus(quotesRes?.data?.status));

      if (
        (quotesRes?.data?.status === "generated_quotes" ||
          quotesRes?.data?.status === "awaiting_webhook_response") &&
        quotesRes?.data?.quotes?.length
      ) {
        const filteredQuotes = (quotesRes?.data?.quotes || []).filter(
          (q) => q.status === "generated"
        );
        const invalidQuotes = (quotesRes?.data?.quotes || []).filter(
          (q) => q.status === "invalid"
        );

        dispatch(setAvailableQuotes(filteredQuotes));
        dispatch(setInvalidQuotes(invalidQuotes));
      }
      
      // Processing complete
      setIsProcessingInstallments(false);
    } catch (error) {
      console.error("Error updating application with installments:", error);
      setIsProcessingInstallments(false);
      // Reopen modal on error
      setIsInstallmentModalOpen(true);
      setInstallmentModalProcessed(false);
    } finally {
      dispatch(setLoadingValue(false));
    }
  };

  const fetchPaymentToken = async () => {
    try {
      const response = await getPaymentTokenAPI({
        submissionID: submission?.id,
      });
      if (response?.data?.success && response?.data?.results?.token) {
        const link = `${window.location.origin}/application/payment?token=${response?.data?.results?.token}`;
        setPaymentLink(link);
      }
    } catch (error) {}
  };

  const onSendQuotesClick = () => {
    if (submission) {
      if (COUNTRY === CountryCode.BR) {
        dispatch(
          sendQuoteForReview({
            quote_id: selectedQuotesForSendingEmail[0].id,
          })
        );
      } else {
        dispatch(
          sendQuotes({
            applicantEmail: submission?.application_params?.applicant_email,
            applicantName: submission?.application_params?.full_name,
            companyName: portalAccount?.company.company_name,
            selectedQuoteIDs: selectedQuotesForSendingEmail.map((sq) => sq.id),
            submissionID: submission?.id,
            userID: portalAccount?.user?.user_id,
          })
        );
      }
    } else {
      if (COUNTRY === CountryCode.BR) {
        dispatch(
          sendQuoteForReview({
            quote_id: selectedQuotesForSendingEmail[0].id,
          })
        );
      } else {
        dispatch(
          sendQuotes({
            applicantEmail: updatedSiaApplication?.params?.applicant_email,
            applicantName: updatedSiaApplication?.params?.full_name,
            companyName: selectedAccount?.name,
            selectedQuoteIDs: selectedQuotesForSendingEmail.map((sq) => sq.id),
            submissionID: submissionId,
            userID: portalAccount?.user?.user_id,
          })
        );
      }
    }
  };

  useEffect(() => {
    dispatch(resetQuotesForSendingEmail());
  }, []);

  // Open installment modal for homeowners-allianz when quotes are loaded FROM APPLICATION
  useEffect(() => {
    if (
      isHomeownersAllianz &&
      !isFromDashboard &&
      quotes.length > 0 &&
      !installmentModalProcessed &&
      packages.length > 0 &&
      justGeneratedFromApplication // Only show if coming from application submission
    ) {
      setIsInstallmentModalOpen(true);
      // Reset the flag so modal doesn't show again if user navigates back
      dispatch(setJustGeneratedFromApplication(false));
    }
  }, [isHomeownersAllianz, isFromDashboard, quotes, installmentModalProcessed, packages, justGeneratedFromApplication]);

  useEffect(() => {
    if (submission) {
      fetchPaymentToken();
    }
  }, [submission, sentQuotesToken]);

  useEffect(() => {
    if (sentQuotesToken && status === "quotes_generated" && onQuotesSent) {
      onQuotesSent();
    }
  }, [sentQuotesToken]);

  useEffect(() => {
    if (sentQuotesToken) {
      localStorage.removeItem("currentApplicantID");
      localStorage.removeItem("prefillData");
    }
  }, [sentQuotesToken]);

  useEffect(() => {
    if (quoteStatus === "awaiting_webhook_response") {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [quoteStatus, startPolling, stopPolling]);

  const RenderSendQuotesButton = () => (
    <CustomButton
      btnText={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            {externalAuthConfig?.partner === "fairfax"
              ? t("QUOTE_PAGE_SEND_PROPOSAL")
              : t("QUOTE_PAGE_SEND_QUOTES")}
          </Typography>
          {sendingQuotes && (
            <CircularProgress size={14} sx={{ color: "white", ml: "4px" }} />
          )}
        </Box>
      }
      data_cy="send-quotes-button"
      onClickCB={handleOpenModal}
      disabled={!selectedQuotesForSendingEmail?.length || sendingQuotes}
    />
  );

  const RenderUpdateApplicationButton = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
      onClick={handleUpdateApplication}
    >
      <AnimatedButton
        variant="outlined"
        sx={{
          position: "relative",
          ml: "20px",
          padding: "8px 0px",
          borderRadius: "12px",
          border: `1px solid ${customStyles?.muiTheme?.palette?.primary?.main}`,
        }}
      >
        <EditOutlined fontSize="small" />
      </AnimatedButton>
    </Box>
  );

  const RenderDownloadQuotePdfButton = () => {
    return (
      <CustomButton
        btnText={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {t("QUOTE_PAGE_DOWNLOAD_QUOTE_PDF")}
            </Typography>
            <DownloadOutlined fontSize="small" sx={{ ml: "4px" }} />
            {downloadingQuotePdf && (
              <CircularProgress size={14} sx={{ ml: "4px" }} />
            )}
          </Box>
        }
        data_cy="download-quote-pdf-button"
        onClickCB={downloadQuotePdf}
      />
    );
  };

  // Show loading while processing installments
  if (isProcessingInstallments) {
    return (
      <Box
        sx={{
          px: { xs: "20px", sm: "40px", md: "20px", lg: "20px" },
          display: "flex",
          minHeight: "60vh",
          alignItems: "center",
          justifyContent: "center",
        }}
        {...animateRouteProps}
      >
        <CircularProgressLoader />
      </Box>
    );
  }

  if (!quotes.length && !polling) {
    return <QuotesMissing invalidQuote={invalidQuotes?.[0]} />;
  }

  if (PORTAL_NAME === FAIRFAX || externalAuthConfig?.partner === "fairfax") {
    // Assumption: There is always one quote for Fairfax
    const lowPremiumError =
      quotes?.[0]?.data?.status?.error?.[CountryToLanguageMapping[COUNTRY]];
    if (lowPremiumError) {
      // Assumption: Technically, quotes are not missing. Just temporarily reusing the same component
      return <QuotesMissing validQuoteError={lowPremiumError} />;
    }
  }

  if (sentQuotesToken && !isFromDashboard) {
    return <SuccessMessage message={t("QUOTE_PAGE_SUCCESS_MESSAGE")} />;
  }

  if (!sentQuotesToken && sentQuotesError && !isFromDashboard) {
    return <ErrorMessage message={sentQuotesError} />;
  }

  // Flags for hiding the section subTitle and description in some portals. For example, Fairfax.
  // More portals can be added in the below array if needed.
  const HIDE_SECTION_SUBTITLE = [FAIRFAX].includes(PORTAL_NAME);
  const HIDE_SECTION_DESCRIPTION = [FAIRFAX].includes(PORTAL_NAME);
  const isAllianzQuote = quotes.some(
    (quote) =>
      quote.product === "AUTO-ALLIANZ" || quote.product === "MOTO-ALLIANZ"
  );
  const showSendQuotesButton =
    (isAllianzQuote ||
      IS_BROKER_PORTAL ||
      externalAuthConfig?.partner === "fairfax") &&
    (status === "quotes_generated" || !status) &&
    !sentQuotesToken;

  // Get blockedAlerts from selected quote if it exists

  const blockedAlerts = selectedQuotes[0]?.data?.blockedAlerts;

  return (
    <>
      <Box
        sx={{
          px: { xs: "20px", sm: "40px", md: "20px", lg: "20px" },
        }}
        {...animateRouteProps}
      >
        {!isFromDashboard && (
          <SectionInfo
            subTitle={
              HIDE_SECTION_SUBTITLE ? "" : t("QUOTE_PAGE_DESCRIPTION_PRIMARY")
            }
            description={
              HIDE_SECTION_DESCRIPTION
                ? ""
                : t("QUOTE_PAGE_DESCRIPTION_SECONDARY")
            }
          />
        )}

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {selectedProducts.length > 1 && (
              <Tabs value={selectedTab} onChange={handleChange}>
                {selectedProducts.map((product, index) => (
                  <Tab
                    key={index}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          textTransform: "none",
                          gap: "6px",
                        }}
                      >
                        {IS_BROKER_PORTAL && (
                          <CheckCircleIcon
                            sx={{
                              transition: "0.3s",
                              background: "white",
                              borderRadius: "12px",
                              color:
                                selectedForSendingEmail(
                                  product?.product?.product_name
                                ) ||
                                sentQuotesToken ||
                                status === "payment_made"
                                  ? "#4CAF50"
                                  : "rgba(0, 0, 0, 0.54)",
                            }}
                          />
                        )}
                        <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                          {product.product.product_name}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            )}
            {showSendQuotesButton && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex" }}>
                  {!isAllianzQuote && <RenderSendQuotesButton />}
                  {(otherConfig?.allowQuotePdfDownload ||
                    externalAuthConfig?.partner === "fairfax" ||
                    isAllianzQuote) && <RenderDownloadQuotePdfButton />}
                  {submission && <RenderUpdateApplicationButton />}
                </Box>
                {blockedAlerts && blockedAlerts.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "#fff3cd",
                      color: "#856404",
                      border: "1px solid #ffeeba",
                      borderRadius: 1,
                    }}
                  >
                    <Typography>{blockedAlerts}</Typography>
                  </Box>
                )}
              </Box>
            )}
            {submission &&
              (status === "quotes_generated" ||
                status === "quotes_sent" ||
                status === "payment_made" ||
                !status) &&
              otherConfig?.allowQuotePdfDownload &&
              // Only show this button if it's not already shown in the showSendQuotesButton section
              (!showSendQuotesButton ||
                !(
                  otherConfig?.allowQuotePdfDownload ||
                  externalAuthConfig?.partner === "fairfax" ||
                  isAllianzQuote
                )) && <RenderDownloadQuotePdfButton />}
            {IS_BROKER_PORTAL && paymentLink && status === "quotes_sent" && (
              <ShareLinkButton paymentLink={paymentLink} />
            )}
          </Box>
          {selectedQuotes?.[0]?.data?.operacao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mr: 5 }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                  {`Operação: ${selectedQuotes[0].data.operacao}`}
                </Typography>
            </Box>
          )}
          {selectedProducts.map((product, index) => {
            let productQuotes = quotes.filter(
              (qt) =>
                qt.product_details?.type === product.product.product_identifier
            );

            // By default, daily quotes are hidden
            if (!quoteConfig?.enableDailyQuote) {
              productQuotes = productQuotes.filter(
                (quote) => quote.premium?.term !== "day"
              );
            }

            return (
              <TabPanel
                key={product?.product?.id}
                value={selectedTab}
                index={index}
              >
                <ProductQuoteCard
                  prodIcon={ProductIconMapping(
                    product.product.product_identifier
                  )}
                  prodTitle={getProductTitle(product)}
                  plans={productQuotes}
                  key={product.product.id}
                />
              </TabPanel>
            );
          })}
         <Summary summaryData={summaryData} />
        </Box>
      </Box>
      <PopupModal
        open={isModalOpen}
        title={
          externalAuthConfig?.partner === "fairfax"
            ? t("POP_UPS.SEND_PROPOSAL_POPUP.title")
            : t("POP_UPS.SEND_QUOTES_POPUP.title")
        }
        content={t("POP_UPS.SEND_QUOTES_POPUP.content")}
        btnText={t("POP_UPS.SEND_QUOTES_POPUP.btnText")}
        onClose={handleCloseModal}
        onSave={onSendQuotesClick}
      />
      {isHomeownersAllianz && !isFromDashboard && (
        <InstallmentSelectionModal
          open={isInstallmentModalOpen}
          onConfirm={handleInstallmentConfirm}
          packages={packages}
          loading={false}
        />
      )}
    </>
  );
};

export default Quote;
